import { QueryKey } from '@tanstack/query-core'
import {
  InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query'
import queryString from 'query-string'
import { useDispatch } from 'react-redux'
import {
  VirtualCardListModel,
  VirtualCardSecureModel,
  VirtualCardSecureResponse,
  VirtualCardType,
} from 'src/models/virtualCard.models'
import {
  VirtualCardPermissionsModel,
  VirtualCardPermissionsResponse,
} from 'src/models/virtualCardPermissions.models'
import {
  GetVirtualCardRevisionsPagination,
  GetVirtualCardRevisionsQueryParams,
  VirtualCardRevisionListModel,
  VirtualCardRevisionListResponse,
} from 'src/models/virtualCardRevision.models'
import { virtualCardsPaginatedQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  RequestConfig,
  useExtendInfiniteQuery,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'
import {
  VirtualCardsDataModel,
  VirtualCardsFiltersBody,
  VirtualCardsResponse,
} from 'src/queries/virtualCards.models'
import { getVirtualCards } from 'src/queries/virtualCards.network'
import { virtualCardsQueryKeys } from 'src/queries/virtualCards.queryKeys'
import {
  GET_VIRTUAL_CARD_FAILURE,
  GET_VIRTUAL_CARD_INIT,
  GET_VIRTUAL_CARD_PERMISSIONS_FAILURE,
  GET_VIRTUAL_CARD_PERMISSIONS_INIT,
  GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS,
  GET_VIRTUAL_CARD_SUCCESS,
} from 'src/redux/actions/data/virtualCards.actions'
import { GetVirtualCardsRequest } from 'src/requests/getVirtualCards.request'

export { virtualCardsQueryKeys } from 'src/queries/virtualCards.queryKeys'
export type {
  VirtualCardsDataModel,
  VirtualCardsFiltersBody,
  VirtualCardsResponse,
} from 'src/queries/virtualCards.models'

const selectVirtualCardListModel = (
  response: InfiniteData<VirtualCardsResponse, number>,
): VirtualCardsDataModel => ({
  virtualCards: response?.pages.flatMap((page) =>
    page.virtualCards.map((txn) => new VirtualCardListModel(txn)),
  ),
  page: response.pageParams.length - 1,
  totalItems: response?.pages[0]?.pagination.totalItems,
})

export const virtualCardQueries = {
  list: (filters: VirtualCardsFiltersBody | undefined, perPage = 50) =>
    infiniteQueryOptions({
      queryKey: virtualCardsQueryKeys.list(filters, { perPage }),
      queryFn: ({ pageParam }) =>
        getVirtualCards({ filters, page: pageParam, count: perPage }),
      enabled: filters !== undefined,
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        getNextExtendPageParam(lastPage.pagination),
      select: selectVirtualCardListModel,
      // Re-fetches happen in place without resetting back to an empty state.
      placeholderData: keepPreviousData,
      // Preserve useExtendInfiniteQuery's "do not replay all prior pages" behavior.
      gcTime: 0,
    }),
}

interface QueryVirtualCardsOptions
  extends ExtendBaseInfiniteQueryOptions<
    VirtualCardsResponse,
    number,
    VirtualCardsDataModel
  > {
  filters: VirtualCardsFiltersBody | undefined
}

export const useQueryVirtualCards = ({
  filters,
  enabled = true,
  perPage = 50,
  ...rest
}: QueryVirtualCardsOptions) => {
  const { queryCacheOptions: _queryCacheOptions, ...queryOptionsRest } = rest
  const query = useInfiniteQuery({
    ...virtualCardQueries.list(filters, perPage),
    enabled: enabled && filters !== undefined,
    ...queryOptionsRest,
  })

  return {
    ...query,
    complete: !query.hasNextPage && !query.isFetching,
  }
}

interface QueryVirtualCardDetailOptions
  extends ExtendBaseQueryOptions<
    { virtualCard: VirtualCardSecureResponse },
    VirtualCardSecureModel
  > {
  virtualCardId: string | undefined
}

export const useQueryVirtualCardDetail = ({
  virtualCardId,
  enabled = true,
  // Single retry matches the legacy single-fetch behaviour and the 404-as-signal
  // contract on the detail surface; the default client retry of 3 would stall the
  // loading gate behind ~7s of backoff before NotFound / the empty-permissions
  // fallback renders.
  retry = 1,
  ...rest
}: QueryVirtualCardDetailOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery<
    { virtualCard: VirtualCardSecureResponse },
    VirtualCardSecureModel
  >({
    queryKey: virtualCardsQueryKeys.detail(virtualCardId),
    queryUrl: () => `/virtualcards/${virtualCardId}`,
    enabled: enabled && virtualCardId !== undefined,
    select: (data) => new VirtualCardSecureModel(data.virtualCard),
    requestConfig: RequestConfig.Vault,
    retry,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_VIRTUAL_CARD_INIT },
    onSuccess: (data) => {
      // Tag the source so reactQueryInterop does not re-invalidate this query
      // off its own refetch (the Pusher/screening paths dispatch the same action
      // without a source and DO trigger invalidation).
      dispatch({
        type: GET_VIRTUAL_CARD_SUCCESS,
        payload: data,
        meta: { source: 'react-query' },
      })
    },
    onError: (error) => {
      dispatch({ type: GET_VIRTUAL_CARD_FAILURE, payload: error })
    },
  })
}

interface QueryVirtualCardPermissionsOptions
  extends ExtendBaseQueryOptions<
    VirtualCardPermissionsResponse,
    VirtualCardPermissionsModel
  > {
  virtualCardId: string | undefined
}

export const useQueryVirtualCardPermissions = ({
  virtualCardId,
  enabled = true,
  // See useQueryVirtualCardDetail: a single retry preserves the 404-as-signal
  // single-fetch behaviour instead of stalling the loading gate behind retries.
  retry = 1,
  ...rest
}: QueryVirtualCardPermissionsOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery({
    queryKey: virtualCardsQueryKeys.permissions(virtualCardId),
    queryUrl: () => `/virtualcards/${virtualCardId}/permissions`,
    enabled: enabled && virtualCardId !== undefined,
    select: (data) => new VirtualCardPermissionsModel(data),
    // TODO: we currently rely on the 404 error for the use-case of a user not having permission
    // and this is an anti-pattern against how react query usually handles error states
    // but this imitates behaviour today of making a single fetch when the user opens the flyout
    retry,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_VIRTUAL_CARD_PERMISSIONS_INIT },
    onSuccess: (data) => {
      dispatch({ type: GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({ type: GET_VIRTUAL_CARD_PERMISSIONS_FAILURE, payload: error })
    },
  })
}

export const usePostShareBillPayVirtualCard = () => {
  return useExtendMutation<
    { virtualCard: VirtualCardSecureResponse },
    { virtualCardId: string; body: { vendorEmail: string } }
  >(MutationMethod.POST, {
    mutationUrl: ({ virtualCardId }) => `/virtualcards/${virtualCardId}/share`,
  })
}

export const usePutVirtualCardSupportCode = () => {
  return useExtendMutation<
    { customerSupportCode: number },
    { virtualCardId: string; body: Record<string, never> }
  >(MutationMethod.PUT, {
    mutationUrl: ({ virtualCardId }) =>
      `/virtualcards/${virtualCardId}/supportcode`,
  })
}

interface VirtualCardRevisionsRespones {
  revisions: VirtualCardRevisionListResponse[]
  pagination: GetVirtualCardRevisionsPagination
}

const selectVirtualCardRevisionListModel = (
  response: InfiniteData<VirtualCardRevisionsRespones, string | undefined>,
  cardType: VirtualCardType | undefined,
  vendorName: string | undefined,
) => ({
  revisions: response?.pages.flatMap((page) =>
    page.revisions.map(
      (vcr) => new VirtualCardRevisionListModel(vcr, cardType, vendorName),
    ),
  ),
})

interface QueryVirtualCardRevisionsOptions
  extends ExtendBaseInfiniteQueryOptions<
    VirtualCardRevisionsRespones,
    string | undefined,
    { revisions: VirtualCardRevisionListModel[] }
  > {
  virtualCardId: string | undefined
  queryParams: GetVirtualCardRevisionsQueryParams | undefined
}

interface QueryVirtualCardRevisionsSelectorOptions {
  cardType?: VirtualCardType
  vendorName?: string
}

// react query example demonstrating a custom implementation of a non-numerical page param
// also shows separation of concerns for options passed to the network query request
// and options that are used to build the model for the front-end
export const useQueryVirtualCardRevisions = (
  {
    virtualCardId,
    queryParams,
    enabled = true,
    ...rest
  }: QueryVirtualCardRevisionsOptions,
  { cardType, vendorName }: QueryVirtualCardRevisionsSelectorOptions,
) => {
  return useExtendInfiniteQuery<
    VirtualCardRevisionsRespones,
    { revisions: VirtualCardRevisionListModel[] },
    QueryKey,
    string | undefined
  >({
    queryKey: virtualCardsQueryKeys.revisions(virtualCardId, queryParams),
    queryUrl: ({ pageParam }) => {
      return `virtualcards/${virtualCardId}/revisions?${queryString.stringify({ ...queryParams, nextCursor: pageParam })}`
    },
    enabled:
      enabled && virtualCardId !== undefined && queryParams !== undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    getPreviousPageParam: (firstPage, _, __) =>
      firstPage.pagination.previousCursor,
    select: (response) =>
      selectVirtualCardRevisionListModel(response, cardType, vendorName),
    initialPageParam: undefined,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...rest,
  })
}

interface QueryVirtualCardsPaginatedOptions
  extends ExtendBaseQueryOptions<VirtualCardsResponse, VirtualCardsDataModel> {
  filters: GetVirtualCardsRequest | undefined
}

export const useQueryVirtualCardsPaginated = ({
  filters,
  enabled = true,
  ...rest
}: QueryVirtualCardsPaginatedOptions) => {
  return useExtendQuery({
    queryKey: virtualCardsPaginatedQueryKeys.list(filters),
    queryUrl: () => `virtualcards?${queryString.stringify({ ...filters })}`,
    enabled: enabled && filters !== undefined,
    select: (data: VirtualCardsResponse) => ({
      virtualCards: data.virtualCards.map(
        (vcn) => new VirtualCardListModel(vcn),
      ),
      page: data.pagination.page,
      totalItems: data.pagination.totalItems,
    }),
    ...rest,
  })
}
